import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  CartesianGrid,
  DotProps,
  Line,
  LineChart,
  XAxis,
  YAxis,
} from 'recharts';
import { CategoricalChartState } from 'recharts/types/chart/types';

import icChevron from '@/assets/ic_chevron.svg';
import { Divider, Loader } from '@/components';
import * as Chart from '@/components/chart';
import { getCampaings } from '@/services/editAccount';
import { ResponseMyCampaings } from '@/types/team';
import { formatNumberToLocale } from '@/utils';

type AwardsMockType = Record<
  'top10k' | 'top100k' | 'top100' | 'top3' | 'top2' | 'winner',
  { date: string; label: string }
>;

const typeColors: Record<keyof AwardsMockType, { icon: string; bg: string }> = {
  top100k: { icon: 'text-gdt-gray-4', bg: 'bg-gdt-gray-6' },
  top10k: { icon: 'text-gdt-status-orange', bg: 'bg-gdt-status-orange-light' },
  top100: { icon: 'text-gdt-status-green', bg: 'bg-gdt-status-green-light' },
  top3: { icon: 'stroke-gdt-blue', bg: 'bg-gdt-gray-6' },
  top2: { icon: 'stroke-gdt-blue', bg: 'bg-gdt-blue-light2' },
  winner: {
    icon: 'stroke-gdt-status-yellow',
    bg: 'bg-gdt-status-yellow-light',
  },
};

interface AwardProps {
  type: keyof AwardsMockType;
}

const IcAward = ({ type }: AwardProps) => (
  <svg
    className={`w-5 h-5 fill-current   ${typeColors[type].icon}`}
    fill="none"
    height="17"
    viewBox="0 0 17 17"
    width="17"
  >
    <path
      clipRule="evenodd"
      d="M8.50001 1.41667C6.1528 1.41667 4.25001 3.31946 4.25001 5.66667C4.25001 8.01388 6.1528 9.91667 8.50001 9.91667C10.8472 9.91667 12.75 8.01388 12.75 5.66667C12.75 3.31946 10.8472 1.41667 8.50001 1.41667ZM2.83334 5.66667C2.83334 2.53705 5.3704 0 8.50001 0C11.6296 0 14.1667 2.53705 14.1667 5.66667C14.1667 8.79628 11.6296 11.3333 8.50001 11.3333C5.3704 11.3333 2.83334 8.79628 2.83334 5.66667Z"
      fillRule="evenodd"
    />
    <path
      clipRule="evenodd"
      d="M11.0914 9.12928C11.4792 9.07783 11.8353 9.3505 11.8867 9.7383L12.7438 16.1983C12.7796 16.4679 12.6578 16.7342 12.4305 16.8835C12.2032 17.0328 11.9104 17.0388 11.6772 16.8989L8.49999 14.9925L5.32275 16.8989C5.08952 17.0388 4.79675 17.0328 4.56942 16.8835C4.34208 16.7341 4.22034 16.4678 4.25615 16.1982L5.11324 9.74528C5.16474 9.35749 5.52087 9.08487 5.90867 9.13638C6.29646 9.18789 6.56908 9.54401 6.51757 9.93181L5.85397 14.928L8.13555 13.5591C8.35987 13.4245 8.6401 13.4245 8.86442 13.5591L11.1462 14.9282L10.4824 9.92462C10.4309 9.53682 10.7036 9.18073 11.0914 9.12928Z"
      fillRule="evenodd"
    />
  </svg>
);

const IcCup = ({ type }: AwardProps) => (
  <svg
    className={`${typeColors[type].icon}`}
    fill="none"
    height="16"
    width="16"
  >
    <path
      d="M8.102 11v1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M4.766 14.668h6.666v-.667c0-.733-.6-1.333-1.333-1.333h-4c-.733 0-1.333.6-1.333 1.333v.667Z"
      strokeMiterlimit="10"
      strokeWidth="1.5"
    />
    <path
      d="M4.102 14.666h8M7.999 10.667a4.663 4.663 0 0 1-4.667-4.666V4a2.666 2.666 0 0 1 2.667-2.667h4a2.666 2.666 0 0 1 2.666 2.667v2A4.663 4.663 0 0 1 8 10.667Z"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M3.646 7.767a3.24 3.24 0 0 1-1.287-.8c-.6-.667-1-1.467-1-2.4A1.65 1.65 0 0 1 3.026 2.9h.433c-.133.307-.2.647-.2 1v2c0 .667.14 1.294.387 1.867ZM12.352 7.767c.5-.16.94-.453 1.286-.8.6-.667 1-1.467 1-2.4A1.65 1.65 0 0 0 12.972 2.9h-.434c.134.307.2.647.2 1v2a4.7 4.7 0 0 1-.386 1.867Z"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

const chartConfig = {
  position: {
    color: 'hsla(225, 100%, 60%, 1)',
  },
} satisfies Chart.ChartConfig;

const CustomTick = ({
  x,
  y,
  payload,
}: {
  x: number;
  y: number;
  payload: { value: string };
}) => {
  const [line1] = payload.value.split(' ');
  const findYear = payload.value
    .split(' ')
    .find((t) => /\d{4}/.exec(t))
    ?.slice(0, 4);
  const label = `${line1.slice(0, 4)}${line1.includes('-') ? '' : '.'}`;
  return (
    <text fill="#A09EB3" fontSize="12" textAnchor="middle" x={x} y={y}>
      <tspan dy="0.6em" x={x}>
        {label}
      </tspan>
      <tspan dy="1.2em" x={x}>
        {findYear}
      </tspan>
    </text>
  );
};

const CustomTooltipContent = ({ payload }: any) => {
  if (payload?.length && payload[0].payload) {
    const data = payload[0].payload;
    if (data.team === 'No participaste' || data.position === 0) {
      return null;
    }

    const N = formatNumberToLocale(payload[0].value);
    return (
      <div className="bg-gdt-blue font-bold text-2xs text-white px-2 py-1 rounded-full">
        {N === '0' ? '-' : N}
      </div>
    );
  }
  return null;
};

const findMaximunPosition = (data: { position: number }[]) => {
  return data.reduce(
    (max, { position }) => (position > max ? position : max),
    0
  );
};

interface ParsedData {
  moment: string;
  position: number;
  bestPosition?: number;
  totalPoints?: number;
  campaign?: number;
  team?: string;
  uniqueId: string;
}

const CustomActiveDot = ({
  cx,
  cy,
  payload,
}: DotProps & { payload?: ParsedData }) => {
  if (cx === undefined || cy === undefined) {
    return null;
  }
  if (
    payload &&
    (payload.team === 'No participaste' || payload.position === 0)
  ) {
    return null;
  }

  return (
    <svg
      className="cursor-pointer"
      fill="none"
      height={10}
      viewBox="0 0 10 10"
      width={10}
      x={cx - 5}
      y={cy - 5}
    >
      <circle cx="5" cy="5" fill="#3366FF" r="5" />
      <circle cx="5" cy="5" fill="white" r="2" />
    </svg>
  );
};

const topDivision = (position: number) => {
  if (position <= 10) {
    return 'Top 10';
  }
  if (position <= 100) {
    return 'Top 100';
  }
  if (position <= 1000) {
    return 'Top 1.000';
  }
  if (position <= 10000) {
    return 'Top 10.000';
  }
  if (position <= 25000) {
    return 'Top 25.000';
  }
  return formatNumberToLocale(position);
};

interface CustomDotProps extends DotProps {
  payload?: ParsedData;
  selectedCampaing: string;
}

const CustomizedDot = ({
  cx,
  cy,
  payload,
  selectedCampaing,
}: CustomDotProps) => {
  if (
    !payload ||
    payload.team === 'No participaste' ||
    payload.position === 0
  ) {
    return null;
  }

  if (payload?.uniqueId === selectedCampaing) {
    return <CustomActiveDot cx={cx} cy={cy} />;
  }
  return null;
};
export const MyCampaigns = () => {
  const [slicedData, setSlicedData] = useState<ParsedData[]>([]);
  const [campaignData, setCampaignData] = useState<ParsedData[]>([]);
  const [rawData, setRawData] = useState<ResponseMyCampaings['output']>();
  const [selectedCampaign, setSelectedCampaign] = useState('');

  const { isLoading } = useQuery('getAwards', async () => getCampaings(), {
    refetchOnWindowFocus: false,
    keepPreviousData: false,
    onSuccess(data) {
      const mappedData = data.output.puntajes.map((item) => ({
        moment:
          data.output.ediciones.find(
            (edicion) => edicion.edicion === item.edicion
          )?.nombreEdicion ?? '',
        position: item.gral.pos,
        bestPosition: item.mejorFechaPorPosicion.pos,
        totalPoints: item.gral.ptos,
        campaign: item.edicion,
        team: item.nombreEquipo,
        uniqueId: item.edicion + '-' + Math.random().toString(36).slice(2),
      }));
      const fallBackData = data.output.ediciones.map((edicion) => ({
        moment: edicion.nombreEdicion,
        position: 0,
        bestPosition: 0,
        totalPoints: 0,
        campaign: edicion.edicion,
        team: 'No participaste',
        uniqueId: edicion.edicion + '-' + Math.random().toString(36).slice(2),
      }));
      const mergedData = [];
      if (mappedData.length < fallBackData.length) {
        mergedData.push(
          ...fallBackData.filter(
            (camp) =>
              !mappedData.some((mapped) => mapped.campaign === camp.campaign)
          ),
          ...mappedData
        );
      } else {
        mergedData.push(...mappedData);
      }
      // make it multiple of 5 if it's not multiple of 5 duplicate the last elements of the next 5 items to make it multiple of 5 with a different uniqueId
      const remainder = mergedData.length % 5;
      if (remainder !== 0) {
        const needed = 5 - remainder;
        const firstElements = mergedData.slice(0, needed).map((item) => ({
          ...item,
          uniqueId: item.campaign + '-' + Math.random().toString(36).slice(2),
          bestPosition: 0,
          totalPoints: 0,
          campaign: 0,
          team: 'No participaste',
          position: 0,
          moment: '-', // Using dash as label for padded elements
        }));
        mergedData.unshift(...firstElements);
      }

      setCampaignData(mergedData);
      setSlicedData(mergedData.slice(-5));
      setRawData(data.output);
      // Instead of using campaign number, use uniqueId:
      setSelectedCampaign(mergedData[mergedData.length - 1]?.uniqueId);
    },
  });
  const uniqueCampaignData = useMemo(() => {
    return campaignData.filter(
      (camp, idx) =>
        campaignData.findIndex((c) => c.uniqueId === camp.uniqueId) === idx
    );
  }, [campaignData]);

  const campaingsChunks = useMemo(
    () =>
      [...campaignData]
        .reverse()
        .reduce((acc, _, index, array) => {
          if (index % 5 === 0) {
            const chunk = array.slice(index, index + 5);

            acc.push(chunk);
          }
          return acc;
        }, [] as ParsedData[][])
        .reverse()
        .map((chunk) => [...chunk].reverse()),
    [campaignData]
  );

  const handleCampaignChange = (key: 'next' | 'back') => {
    // Find the current index in the unique campaigns list
    const currentIndex = uniqueCampaignData.findIndex(
      (camp) => camp.uniqueId === selectedCampaign
    );
    if (currentIndex === -1 || (currentIndex === 0 && key === 'back')) return;

    // Calculate new index in the unique list
    let newIndex = key === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (newIndex < 0 || newIndex >= uniqueCampaignData.length) return;

    while (
      newIndex >= 0 &&
      newIndex < uniqueCampaignData.length &&
      (uniqueCampaignData[newIndex].team === 'No participaste' ||
        uniqueCampaignData[newIndex].position === 0)
    ) {
      newIndex = key === 'next' ? newIndex + 1 : newIndex - 1;
    }

    const newCampaign = uniqueCampaignData[newIndex];

    // Find chunks based on uniqueId now within the unique set
    const findChunk =
      campaingsChunks.length -
      1 -
      [...campaingsChunks]
        .reverse()
        .findIndex((chunk) =>
          chunk.some((camp) => camp.uniqueId === newCampaign.uniqueId)
        );
    const previousChunk =
      campaingsChunks.length -
      1 -
      [...campaingsChunks]
        .reverse()
        .findIndex((chunk) =>
          chunk.some((camp) => camp.uniqueId === selectedCampaign)
        );

    if (
      (key === 'next' && findChunk > previousChunk) ||
      (key === 'back' && findChunk < previousChunk)
    ) {
      setSlicedData(campaingsChunks[findChunk]);
      const newCampaignFromChunk =
        key === 'next'
          ? campaingsChunks[findChunk][0]
          : campaingsChunks[findChunk][campaingsChunks[findChunk].length - 1];
      setSelectedCampaign(newCampaignFromChunk.uniqueId);
    } else {
      setSelectedCampaign(newCampaign.uniqueId);
    }
  };
  const handleLineClick = (data: CategoricalChartState) => {
    if ('activePayload' in data === false) {
      return;
    }
    const clickedCampaign = data?.activePayload?.[0].payload;
    // Prevent selecting "No participaste" entries
    if (
      clickedCampaign?.team !== 'No participaste' &&
      clickedCampaign?.position !== 0
    ) {
      setSelectedCampaign(clickedCampaign.uniqueId);
    }
  };

  const selectedCampaignData = campaignData.find(
    (camp) => camp.uniqueId === selectedCampaign
  );

  // find best position on all dates that are not 0
  const bestPositionOverall = campaignData.length
    ? campaignData.reduce((prev, curr) => {
        if (curr.position === 0) {
          return prev;
        }
        if (prev.position === 0) {
          return curr;
        }
        return curr.position !== undefined &&
          (prev.position === undefined || curr.position < prev.position)
          ? curr
          : prev;
      }, campaignData[0])
    : undefined;

  const bestPositionOnADate = campaignData.length
    ? campaignData.reduce((prev, curr) => {
        if (curr.bestPosition === 0) {
          return prev;
        }
        if (prev.bestPosition === 0) {
          return curr;
        }
        return curr.bestPosition !== undefined &&
          (prev.bestPosition === undefined ||
            curr.bestPosition < prev.bestPosition)
          ? curr
          : prev;
      }, campaignData[0])
    : undefined;

  const bestPositionOnADateEdicion = bestPositionOnADate
    ? rawData?.ediciones.find(
        (edicion) => edicion.edicion === bestPositionOnADate.campaign
      )?.nombreEdicion
    : undefined;

  const getYAxisDomain = (data: { position: number }[]) => {
    const maxPosition = findMaximunPosition(data);
    if (maxPosition === 0) {
      // No data: force a default domain that puts the line at the bottom
      return [0, 100];
    }
    return [`dataMin - ${0.3 * maxPosition}`, `dataMax + ${0.3 * maxPosition}`];
  };
  if (isLoading || !rawData) {
    return <Loader />;
  }

  return (
    // <EditMyAccountLayout
    //   goBackTo={ROUTES.editAccount.menu}
    //   title="Mis campañas anteriores"
    // >
    <>
      <div className="flex flex-col lg:ml-[115px] lg:mt-10 mt-4 px-4 lg:px-0 lg:pr-[40px]">
        <h2 className="text-lg font-bold ">Récords personales</h2>
        {rawData?.puntajes.length === 0 ? (
          <p className="font-light mt-2">
            No participaste de ediciones anteriores de Gran DT
          </p>
        ) : (
          <>
            <div className="flex gap-x-5 mb-6 mt-4">
              {/* Contenedor 1 */}
              <div className="rounded-lg md:shadow-[0px_16px_19px_0px_rgba(0,_0,_0,_0.1)] max-w-44 bg-white p-3 flex flex-col">
                <div className="flex items-center justify-between min-h-[40px]">
                  <h3 className="text-gdt-gray-4 font-bold text-xs leading-tight">
                    MEJOR POSICIÓN EN UN TORNEO
                  </h3>
                  <div className={`rounded-full p-2 ${typeColors.winner.bg}`}>
                    <IcCup type="winner" />
                  </div>
                </div>

                <div className="flex flex-col mt-2">
                  <span className="text-xl font-bold leading-none">
                    {formatNumberToLocale(bestPositionOverall?.position ?? 0)}
                  </span>
                  <span className="text-xs font-bold text-gdt-status-yellow mt-1">
                    {bestPositionOverall?.moment}
                  </span>
                </div>
              </div>

              {/* Contenedor 2 */}
              <div className="rounded-lg md:shadow-[0px_16px_19px_0px_rgba(0,_0,_0,_0.1)] max-w-44 bg-white p-3 flex flex-col">
                <div className="flex items-center justify-between min-h-[40px]">
                  <h3 className="text-gdt-gray-4 font-bold text-xs leading-tight">
                    MEJOR POSICIÓN EN UNA FECHA
                  </h3>
                  <div className={`rounded-full p-2 ${typeColors.top100.bg}`}>
                    <IcAward type="top100" />
                  </div>
                </div>

                <div className="flex flex-col mt-2">
                  <span className="text-xl font-bold leading-none">
                    {formatNumberToLocale(
                      bestPositionOnADate?.bestPosition ?? 0
                    )}
                  </span>
                  <span className="text-xs font-bold text-gdt-status-green mt-1">
                    {bestPositionOnADateEdicion} - Fecha{' '}
                    {bestPositionOnADate?.campaign}
                  </span>
                </div>
              </div>
            </div>

            <Divider type="horizontal" />
            <h2 className="text-lg font-bold mt-6 md:mt-10">
              Historial de campañas
            </h2>

            <div className="flex justify-between w-full lg:max-w-[669px] mt-6 mb-4 md:mb-0 md:mt-0">
              <div className="md:mx-auto px-6 md:px-0 max-md:w-full flex items-center justify-between gap-x-5 mt-2">
                <button
                  className="w-7 h-7 rounded-lg bg-gdt-blue-light2 flex justify-center items-center rotate-180"
                  onClick={() => handleCampaignChange('back')}
                >
                  <img
                    alt="Gran DT - flecha derecha"
                    className="w-3 h-3"
                    src={icChevron}
                  />
                </button>
                <span className="text-gdt-blue-dark font-bold text-sm">
                  {selectedCampaignData?.moment}
                </span>
                <button
                  className="w-7 h-7 rounded-lg bg-gdt-blue-light2 flex justify-center items-center"
                  onClick={() => handleCampaignChange('next')}
                >
                  <img
                    alt="Gran DT - flecha derecha"
                    className="w-3 h-3"
                    src={icChevron}
                  />
                </button>
              </div>
            </div>
            <div className="rounded-lg md:shadow-[0px_16px_19px_0px_rgba(0,_0,_0,_0.1)] max-w-[669px] bg-white p-3 flex flex-col gap-y-2 mt-3">
              <div className="flex items-center gap-x-1">
                <div className="w-2 h-2 bg-gdt-blue-header rounded-full" />
                <h4 className="text-gdt-blue-header font-bold text-xs">
                  Posición
                </h4>
              </div>
              <div className="flex flex-col md:flex-row gap-2 justify-between gap-x-6 pr-3">
                <div className="w-[328px] h-56">
                  <Chart.ChartContainer
                    className="h-full w-full"
                    config={chartConfig}
                  >
                    <LineChart
                      accessibilityLayer
                      data={slicedData}
                      margin={{
                        bottom: 10,
                        left: 20,
                        right: 15,
                        top: 10,
                      }}
                      onClick={handleLineClick}
                    >
                      <CartesianGrid
                        horizontal={false}
                        stroke="hsla(180, 5%, 83%, 1)"
                        strokeOpacity={1}
                        vertical={true}
                      />
                      <XAxis
                        axisLine={false}
                        dataKey="moment"
                        tick={CustomTick}
                        tickFormatter={(value) => value.slice(0, 3)}
                        tickLine={false}
                        tickMargin={8}
                      />
                      <YAxis
                        hide
                        axisLine={false}
                        domain={getYAxisDomain(slicedData)}
                        tick={false}
                        type="number"
                      />

                      <Chart.ChartTooltip
                        content={<CustomTooltipContent />}
                        cursor={false}
                      />
                      <Line
                        activeDot={(props: DotProps) => (
                          <CustomActiveDot {...props} />
                        )}
                        dataKey="position"
                        dot={
                          <CustomizedDot selectedCampaing={selectedCampaign} />
                        }
                        stroke="var(--color-position)"
                        strokeWidth={2}
                        type="linear"
                      />
                    </LineChart>
                  </Chart.ChartContainer>
                </div>
                <div className="flex flex-col w-full gap-y-7 mt-6 md:mt-0">
                  <div className="grid grid-cols-12 items-center  w-full">
                    <div className="flex flex-col gap-y-2 col-span-8">
                      <span className="text-gdt-gray-4 font-bold text-xs">
                        Tu equipo
                      </span>
                      <span className="text-lg font-bold">
                        {selectedCampaignData?.team}
                      </span>
                    </div>
                    <div className="col-span-4">
                      {selectedCampaignData?.position &&
                      selectedCampaignData.position <= 25000 ? (
                        <span className="bg-gdt-status-green-light text-gdt-status-green font-bold text-xs rounded-lg py-1 px-2">
                          {topDivision(selectedCampaignData?.position ?? 0)}
                        </span>
                      ) : (
                        <span />
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-y-3">
                    <div className="flex justify-between">
                      <span className="text-gdt-gray-4 font-semibold text-xs">
                        POS. EN EL TORNEO
                      </span>
                      <span className="text-sm font-bold text-gdt-gray-2">
                        {selectedCampaignData?.position === 0
                          ? '-'
                          : formatNumberToLocale(
                              selectedCampaignData?.position ?? 0
                            )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gdt-gray-4 font-semibold text-xs">
                        MEJOR POS. EN UNA FECHA
                      </span>
                      <span className="text-sm font-bold text-gdt-gray-2">
                        {selectedCampaignData?.bestPosition === 0
                          ? '-'
                          : formatNumberToLocale(
                              selectedCampaignData?.bestPosition ?? 0
                            )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gdt-gray-4 font-semibold text-xs">
                        PUNTAJE TOTAL OBTENIDO
                      </span>
                      <span className="text-sm font-bold text-gdt-gray-2">
                        {selectedCampaignData?.totalPoints === 0
                          ? '-'
                          : formatNumberToLocale(
                              selectedCampaignData?.totalPoints ?? 0
                            )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};
